import {
  createBranchMutation,
  getBranchesQuery,
  handleApiResponse,
  queryKeys,
} from "@comandero/shared";
import { createPowerSyncQuery } from "@comandero/shared/solid";
import { type Branch, desc, type Organization, organization } from "@comandero/powersync";
import { type Accessor, createEffect, createMemo } from "solid-js";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { activeRestaurantIdFor, setActiveRestaurantIdFor } from "@/features/pos/active-restaurant";

export interface ActiveContext {
  /** Active restaurant (organization). */
  org: Accessor<Organization | null>;
  /** All user restaurants synced locally (switcher). */
  orgs: Accessor<Organization[]>;
  /** Sucursal activa. */
  branch: Accessor<Branch | null>;
  branches: Accessor<Branch[]>;
  isReady: Accessor<boolean>;
  /** Changes active restaurant (persists per user; re-scopes entire POS). */
  setActiveRestaurant: (orgId: string) => void;
  /**
   * Creates a restaurant (atomic org server-side), waits for it to sync to
   * local, sets it as ACTIVE and returns its id. Serves both for
   * onboarding (first restaurant) and for adding additional restaurants.
   */
  bootstrapRestaurant: (name: string) => Promise<string>;
  /** Creates an additional branch in the active restaurant. */
  addBranch: (name: string) => Promise<string | null>;
}

/**
 * MODULE-LEVEL guard for default branch: with the switcher in the
 * header there are SEVERAL live instances of `useActiveContext` at once (header +
 * page); an instance-level guard would let each create its own duplicate "Main"
 * for the same new org.
 */
const autoBranchHandled = new Set<string>();

/**
 * Operational POS context: active restaurant (org) + branch.
 *
 * Org-centric model: the restaurant is the organization; the `member` is created
 * atomically on the server (CC-3) via `createOrganization`. The default branch
 * is created locally and syncs via the CRUD queue.
 *
 * Multi-restaurant: the user can belong to multiple orgs; active is
 * a preference persisted per user (`active-restaurant.ts`) validated
 * against local orgs (fallback: the first). All domain queries derive
 * from `org()`/`branch()`, so a switch re-scopes the entire POS
 * reactively.
 */
export function useActiveContext(): ActiveContext {
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const session = useSession();

  const orgsQuery = createPowerSyncQuery<Organization>({
    queryKey: () => queryKeys.comandero.organizations(),
    queryFn: () => {
      const d = drizzle();
      if (!d) {
        return null;
      }
      return d.select().from(organization).orderBy(desc(organization.created_at));
    },
    db,
  });

  const orgs = createMemo<Organization[]>(() => orgsQuery.data ?? []);

  const org = createMemo<Organization | null>(() => {
    const all = orgs();
    if (all.length === 0) {
      return null;
    }
    // Persisted preference validated against what exists locally; if the
    // pointer became stale (deleted/unsynced org) falls back to the first:
    // never blocks (self-healing, same criterion as the ticket pointer).
    const preferred = activeRestaurantIdFor(session.userId());
    return all.find((candidate) => candidate.id === preferred) ?? all[0]!;
  });

  const setActiveRestaurant = (orgId: string): void => {
    setActiveRestaurantIdFor(session.userId(), orgId);
  };

  const branchesQuery = createPowerSyncQuery<Branch>({
    queryKey: () => queryKeys.comandero.branches(org()?.id ?? "none"),
    queryFn: () => {
      const d = drizzle();
      const o = org();
      if (!d || !o?.id) {
        return null;
      }
      return getBranchesQuery(d, o.id);
    },
    db,
    enabled: () => Boolean(org()?.id),
  });

  const branches = createMemo<Branch[]>(() => branchesQuery.data ?? []);
  const branch = createMemo<Branch | null>(() => branches()[0] ?? null);
  const isReady = createMemo(() => Boolean(org()?.id) && Boolean(branch()?.id));

  // Safety net: if the org is already in local but does not yet have a branch
  // (e.g. registration created the org but the branch did not get written), creates
  // a default one. Idempotent across ALL instances of the hook (module
  // guard): only fires once per org.
  createEffect(() => {
    const d = drizzle();
    const o = org();
    if (!d || !o?.id) {
      return;
    }
    if (branchesQuery.isFetching) {
      return;
    }
    if (branches().length > 0) {
      return;
    }
    if (autoBranchHandled.has(o.id)) {
      return;
    }
    autoBranchHandled.add(o.id);
    void createBranchMutation(d, o.id, "Principal");
  });

  async function bootstrapRestaurant(name: string): Promise<string> {
    const d = drizzle();
    if (!d) {
      throw new Error("Local database is not ready");
    }
    // With multi-restaurant there may already be orgs locally: capture prior
    // ids to wait for the NEW org (not "any org", which with an
    // existing one would resolve instantly with the wrong one).
    const before = new Set(orgs().map((existing) => existing.id));
    const slug = name
      .trim()
      .toLowerCase()
      .replaceAll(/[^a-z0-9]+/g, "-")
      .replaceAll(/^-+|-+$/g, "")
      .slice(0, 40);
    // `openapi-fetch` DOES NOT throw on 4xx/5xx (returns `{error}`): without this
    // wrapper, a 401 (e.g. expired session) died silently and the poll
    // below waited 30s for an org that would never arrive — the "Create" button
    // remained stuck without feedback (onboarding bug, CC-8 family).
    await handleApiResponse(
      api.POST("/api/org/createOrganization", {
        body: { name: name.trim(), slug: slug || `r-${Date.now()}` },
      }),
    );
    // The response only confirms `success`; the id arrives when the org syncs
    // to local. We wait for it to appear; the default branch is created by the
    // reactive effect above as soon as the org exists locally.
    const orgId = await waitForNewOrgLocal(d, before);
    if (!orgId) {
      throw new Error("Restaurant did not sync to local in time");
    }
    // The newly created org becomes the active one (onboarding: it was the only one;
    // additional creation: user just requested it — switching is the expectation).
    setActiveRestaurant(orgId);
    return orgId;
  }

  async function addBranch(name: string): Promise<string | null> {
    const d = drizzle();
    const o = org();
    if (!d || !o?.id) {
      return null;
    }
    return createBranchMutation(d, o.id, name);
  }

  return {
    org,
    orgs,
    branch,
    branches,
    isReady,
    setActiveRestaurant,
    bootstrapRestaurant,
    addBranch,
  };
}

const POLL_INTERVAL_MS = 200;
const MAX_WAIT_MS = 30_000;

type DrizzleClient = NonNullable<ReturnType<ReturnType<typeof useDrizzle>>>;

/**
 * Waits (with local polling) for an organization to appear that was NOT in
 * `before` and returns its id (the most recent of the new ones). In registration it is the
 * newly created one, even if the user already had other restaurants.
 */
async function waitForNewOrgLocal(
  d: DrizzleClient,
  before: ReadonlySet<string>,
): Promise<string | null> {
  const start = Date.now();
  while (Date.now() - start < MAX_WAIT_MS) {
    const rows = await d.select().from(organization).orderBy(desc(organization.created_at));
    const fresh = rows.find((row) => !before.has(row.id));
    if (fresh?.id) {
      return fresh.id;
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  return null;
}
